import { useEffect, useCallback } from 'react';
import { ApolloError, useQuery } from '@apollo/client';

import { useSnackbar } from '../providers/snackbar/hook/useSnackbar';
import { useAccount } from '../providers/account/hook/useAccount';
import { GET_CONNECTOR_SELECTIONS, GET_CONNECTORS } from '../utils/gql/queries';
import {
  ConnectorSelection,
  ConnectorType,
  Query,
  QueryGetConnectorsArgs,
} from '../utils/gql/types';

const useBackendConnectors = () => {
  const snackbar = useSnackbar();
  const { user } = useAccount();
  const magentoConnectorEnabled = user?.magentoConnectorEnabled === true;

  const onError = useCallback(
    (err: ApolloError) => {
      const { graphQLErrors, message: errorText } = err;
      const message =
        graphQLErrors && graphQLErrors.length > 0
          ? graphQLErrors[0].message
          : errorText;
      snackbar(message);
    },
    [snackbar],
  );

  const {
    loading: getConnectorsLoading,
    data: connectorsData,
    error: connectorsError,
  } = useQuery<Pick<Query, 'getConnectors'>, QueryGetConnectorsArgs>(
    GET_CONNECTORS,
    {
      fetchPolicy: 'network-only',
    },
  );

  const {
    loading: connectorSelectionsLoading,
    data: connectorSelectionsData,
    error: connectorSelectionsError,
  } = useQuery<Pick<Query, 'getConnectorSelections'>>(
    GET_CONNECTOR_SELECTIONS,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  );

  // Handle connectors error
  useEffect(() => {
    if (connectorsError) {
      onError(connectorsError);
    }
  }, [connectorsError, onError]);

  // Handle connector selections error
  useEffect(() => {
    if (connectorSelectionsError) {
      onError(connectorSelectionsError);
    }
  }, [connectorSelectionsError, onError]);

  const getConnectorId = (type: ConnectorType) =>
    connectorsData?.getConnectors.find(
      (account) => account.connectorType === type,
    )?.id;

  const getConnectorSelection = (
    type: ConnectorType,
  ): ConnectorSelection | undefined =>
    connectorSelectionsData?.getConnectorSelections.find(
      (selection) => selection.connectorType === type,
    );

  const googleAdsConnectorId = getConnectorId(ConnectorType.GOOGLE_ADS);
  const shopifyConnectorId = getConnectorId(ConnectorType.SHOPIFY);
  const facebookConnectorId = getConnectorId(ConnectorType.FACEBOOK);
  const googleAnalytics4ConnectorId = getConnectorId(
    ConnectorType.GOOGlE_ANALYTICS4,
  );
  const googleSearchConsoleConnectorId = getConnectorId(
    ConnectorType.GOOGLE_SEARCH_CONSOLE,
  );
  const databaseConnectorId = getConnectorId(ConnectorType.DATABASE);
  const magentoConnectorId = magentoConnectorEnabled
    ? getConnectorId(ConnectorType.MAGENTO)
    : undefined;

  const clientAccountId =
    getConnectorSelection(ConnectorType.GOOGLE_ADS)?.selectedAccountIds?.[0] ||
    '';
  const propertyId =
    getConnectorSelection(ConnectorType.GOOGlE_ANALYTICS4)
      ?.selectedSubaccountIds?.[0] || '';
  // GSC: site URL salvato come account id in selectedIds.
  const searchConsoleSiteUrl =
    getConnectorSelection(ConnectorType.GOOGLE_SEARCH_CONSOLE)
      ?.selectedAccountIds?.[0] || '';

  return {
    propertyId,
    searchConsoleSiteUrl,
    analyticsAccountLoading: connectorSelectionsLoading,
    accountsLoading: connectorSelectionsLoading,
    clientAccountId,
    googleAdsConnectorId,
    shopifyConnectorId,
    facebookConnectorId,
    googleAnalytics4ConnectorId,
    googleSearchConsoleConnectorId,
    databaseConnectorId,
    magentoConnectorId,
    shouldRedirect: false,
    ready: !(getConnectorsLoading || connectorSelectionsLoading),
  };
};

export default useBackendConnectors;
